/**
 * generateInvoice.js
 * Client-side PDF invoice generator using jsPDF only — no autoTable.
 *
 * Tax numbers are fetched from the backend's per-order TaxCollection
 * snapshot (GET /invoice/generate/:order_number) — NOT recomputed from
 * a live tax config. A past order's invoice must always reflect the
 * tax rates that were actually in effect when it was placed, even if
 * today's rates have since changed.
 *
 * Combo lines are shown as a priced, bold "combo" row followed by one
 * unpriced, muted row per dish inside it (name + qty only), mirroring
 * how OrderList / OrderStatus display combo contents elsewhere in the
 * app. The backend (enrichItems + enrichCombos) flags each row with
 * isComboHeader / isComboDish so this file never has to re-derive
 * which rows belong to a combo.
 *
 * npm install jspdf
 */

import { jsPDF } from 'jspdf';
import { host } from './script/variables';

/* ── Palette ──────────────────────────────────────────────────── */
const C = {
  red:        [192, 57,  43],
  dark:       [15,  25,  35],
  mid:        [74,  85,  104],
  light:      [248, 249, 251],
  border:     [225, 230, 235],
  white:      [255, 255, 255],
  green:      [16,  185, 129],
  greenLight: [209, 250, 229],
  amber:      [245, 158, 11],
  amberLight: [255, 251, 235],
  indigo:     [99,  102, 241],
  indigoLt:   [238, 242, 255],
  rowAlt:     [252, 252, 253],
  headBg:     [230, 232, 236],
  muted:      [148, 158, 170],
};

const QUOTES = [
  'Good food is the foundation of genuine happiness. Thank you for dining with us!',
  "Every meal is a memory. We hope today's was a great one.",
  'Food tastes better when shared — come back with more friends next time!',
  'Life is uncertain. Eat dessert first. See you soon!',
  'One cannot think well, love well, sleep well, if one has not dined well.',
  'Thank you for choosing us. May every bite have been an absolute delight!',
  'We cook with love, and we hope you tasted every bit of it.',
  'A great meal is an act of generosity — thank you for letting us serve you.',
];

/* ── Tiny helpers ─────────────────────────────────────────────── */
const randomQuote = () => QUOTES[Math.floor(Math.random() * QUOTES.length)];
const fmt  = (n) => `Rs. ${Number(n).toFixed(2)}`;
const fmtI = (n) => `Rs.${Number(n).toFixed(2)}`;   // compact for tight cells

const fill   = (doc, rgb) => doc.setFillColor(...rgb);
const stroke = (doc, rgb) => doc.setDrawColor(...rgb);
const color  = (doc, rgb) => doc.setTextColor(...rgb);

const rect  = (doc, x, y, w, h, style = 'F') => doc.rect(x, y, w, h, style);
const rrect = (doc, x, y, w, h, r = 4, style = 'F') => doc.roundedRect(x, y, w, h, r, r, style);

const line = (doc, x1, y1, x2, y2, rgb = C.border, lw = 0.4) => {
  doc.setLineWidth(lw);
  stroke(doc, rgb);
  doc.line(x1, y1, x2, y2);
};

const text = (doc, str, x, y, opts = {}) => doc.text(String(str ?? ''), x, y, opts);

const formatDate = (ts) => {
  if (!ts) return '—';
  return new Date(ts).toLocaleString('en-IN', {
    day: '2-digit', month: 'short', year: 'numeric',
    hour: '2-digit', minute: '2-digit', hour12: true,
  });
};

const safeN = (v) => (!isNaN(Number(v)) ? Number(v) : 0);

/* ── Financials shaping ───────────────────────────────────────────
   Normalizes the API's `financials` payload (sourced from the
   order's TaxCollection snapshot) into the shape this file's
   drawing code uses. No tax amount is calculated here — everything
   is exactly what the backend recorded at order-completion time.
──────────────────────────────────────────────────────────────── */
const shapeFinancials = (financials = {}) => {
  const taxBreakdown = (financials.taxBreakdown || []).map((t) => ({
    group:  t.type || t.name,
    label:  t.name,
    rate:   safeN(t.rate),
    amount: safeN(t.amount),
  }));
  const combinedTaxRate = taxBreakdown.reduce((s, t) => s + t.rate, 0);

  return {
    taxableSub:    safeN(financials.taxableSub),
    nonTaxableSub: safeN(financials.nonTaxableSub),
    taxBreakdown,
    combinedTaxRate,
    totalTax:   safeN(financials.totalTax),
    grandTotal: safeN(financials.grandTotal),
    isEstimate: financials.source === 'fallback-no-snapshot',
  };
};

/* ── Items table row shaping ───────────────────────────────────────
   Single pass over the (already-flattened) items[] payload:
     - assigns display row numbers to priced rows only (dish rows and
       combo header rows) — unpriced combo dish-breakdown rows don't
       get their own number, since they're a sub-listing of the combo
       row directly above them
     - builds the string[] row for drawTable
     - builds the parallel rowMeta[] (bold for combo headers, muted
       for combo dish breakdown) so drawTable can style them
   Returns { rows, rowMeta }.
──────────────────────────────────────────────────────────────── */
const shapeItemRows = (items = []) => {
  const rows = [];
  const rowMeta = [];
  let rowNum = 0;

  items.forEach((item) => {
    if (item.isComboDish) {
      rows.push(['', item.name || '—', '—', '—', item.qty != null ? String(item.qty) : '—', '—', '—', '—']);
      rowMeta.push({ bold: false, muted: true });
      return;
    }

    rowNum += 1;
    rows.push([
      String(rowNum),
      item.name || '—',
      item.size || '—',
      item.isTaxable ? 'Yes' : 'No',
      String(item.qty),
      fmtI(item.unitPrice),
      item.discount > 0 ? `${item.discount}%` : '—',
      fmtI(item.lineNet),
    ]);
    rowMeta.push({ bold: !!item.isComboHeader, muted: false });
  });

  return { rows, rowMeta };
};

/* ── Low-level table renderer ─────────────────────────────────────
   cols: [{ label, width, align:'left'|'center'|'right' }]
   rows: string[][] — each row is a flat array of cell strings
   rowMeta: optional array parallel to `rows`, e.g. { bold: true } for
     a combo's own priced row, or { muted: true } for its unpriced
     dish-breakdown rows — lets those render visually distinct from
     regular item rows without changing the column layout.
   options.headBg, options.rowH, options.fontSize, options.headFontSize
   Returns: finalY
──────────────────────────────────────────────────────────────── */
const drawTable = (doc, startX, startY, cols, rows, options = {}, rowMeta = []) => {
  const {
    rowH         = 22,
    headH        = 24,
    fontSize     = 8.5,
    headFontSize = 8,
    headBgColor  = C.dark,
    headColor    = C.white,
    bodyColor    = C.dark,
    borderColor  = C.border,
    altRow       = true,
    cellPadX     = 7,
  } = options;

  const totalW = cols.reduce((s, c) => s + c.width, 0);

  /* ── header ── */
  fill(doc, headBgColor);
  rect(doc, startX, startY, totalW, headH);

  doc.setFont('helvetica', 'bold');
  doc.setFontSize(headFontSize);
  color(doc, headColor);

  let cx = startX;
  cols.forEach((col) => {
    const align = col.align || 'left';
    let tx = cx + cellPadX;
    let opts = {};
    if (align === 'center') { tx = cx + col.width / 2; opts = { align: 'center' }; }
    if (align === 'right')  { tx = cx + col.width - cellPadX; opts = { align: 'right' }; }
    text(doc, col.label, tx, startY + headH / 2 + headFontSize / 3, opts);
    cx += col.width;
  });

  /* ── body rows ── */
  let y = startY + headH;

  rows.forEach((row, ri) => {
    const meta = rowMeta[ri] || {};
    const bg = altRow && ri % 2 === 1 ? C.rowAlt : C.white;
    fill(doc, bg);
    rect(doc, startX, y, totalW, rowH);

    const baseFontStyle = meta.bold ? 'bold' : (meta.muted ? 'italic' : 'normal');
    const baseColor      = meta.muted ? C.muted : bodyColor;

    doc.setFont('helvetica', baseFontStyle);
    doc.setFontSize(fontSize);
    color(doc, baseColor);

    cx = startX;
    row.forEach((cell, ci) => {
      const col   = cols[ci];
      const align = col?.align || 'left';
      let tx = cx + cellPadX;
      let opts = {};
      if (align === 'center') { tx = cx + col.width / 2; opts = { align: 'center' }; }
      if (align === 'right')  { tx = cx + col.width - cellPadX; opts = { align: 'right' }; }

      // special colouring for GST/tax cell — skipped for muted (combo
      // dish breakdown) rows, which always show '—' there anyway
      if (col?.isGstCol && !meta.muted) {
        color(doc, cell === 'Yes' ? C.green : C.mid);
        doc.setFont('helvetica', cell === 'Yes' ? 'bold' : 'normal');
      }

      text(doc, cell, tx, y + rowH / 2 + fontSize / 3, opts);

      if (col?.isGstCol && !meta.muted) {
        color(doc, baseColor);
        doc.setFont('helvetica', baseFontStyle);
      }

      cx += col?.width || 0;
    });

    // bottom border
    line(doc, startX, y + rowH, startX + totalW, y + rowH, borderColor, 0.3);
    y += rowH;
  });

  // outer border
  stroke(doc, borderColor);
  doc.setLineWidth(0.5);
  doc.rect(startX, startY, totalW, headH + rows.length * rowH, 'S');

  return y;  // finalY
};

/* ═══════════════════════════════════════════════════════════════
   PDF BUILDER — pure function of the API payload, no side fetching
═══════════════════════════════════════════════════════════════ */
const buildPDF = (data) => {
  const { invoice, restaurant, customer, order, items } = data;
  const f = shapeFinancials(data.financials);

  const doc = new jsPDF({ unit: 'pt', format: 'a4' });
  const W   = doc.internal.pageSize.getWidth();
  const PAD = 40;

  const invNum = invoice.orderNumber || String(invoice.orderId || '').slice(-6).toUpperCase() || '—';

  /* ══════════════════════════════════════════
     1 — HEADER BANNER
  ══════════════════════════════════════════ */
  fill(doc, C.dark);
  rrect(doc, PAD, 24, W - PAD * 2, 90, 6);

  color(doc, C.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(21);
  text(doc, restaurant.name || 'Restaurant', PAD + 20, 56);

  if (restaurant.address) {
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    color(doc, [170, 185, 200]);
    text(doc, restaurant.address, PAD + 20, 72);
  }

  fill(doc, C.red);
  rrect(doc, W - PAD - 114, 34, 104, 30, 4);
  color(doc, C.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(14);
  text(doc, 'INVOICE', W - PAD - 62, 55, { align: 'center' });

  color(doc, [160, 180, 200]);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  text(doc, `# ${invNum}`, W - PAD - 62, 75, { align: 'center' });
  text(doc, formatDate(invoice.placedAt), W - PAD - 62, 88, { align: 'center' });

  /* ══════════════════════════════════════════
     2 — META PILLS ROW
  ══════════════════════════════════════════ */
  let y = 130;
  const pills = [
    { label: 'PAYMENT METHOD', value: order.paymentMethod  || '—' },
    { label: 'PAYMENT STATUS', value: order.paymentStatus  || '—' },
    { label: 'ORDER STATUS',   value: order.deliveryStatus || '—' },
  ];
  const pillW = (W - PAD * 2 - 12) / 4;

  pills.forEach((p, i) => {
    const px = PAD + i * (pillW + 4);
    fill(doc, C.light);
    stroke(doc, C.border);
    doc.setLineWidth(0.5);
    rrect(doc, px, y, pillW, 36, 4, 'FD');

    color(doc, [140, 155, 170]);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    text(doc, p.label, px + pillW / 2, y + 13, { align: 'center' });

    color(doc, C.dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(9.5);
    text(doc, p.value, px + pillW / 2, y + 27, { align: 'center' });
  });

  /* ══════════════════════════════════════════
     3 — ITEMS TABLE
     `items` mixes standalone dish rows, combo "header" rows (priced,
     taxed — bold), and combo dish-breakdown rows (unpriced, indented,
     italic/muted), flagged via item.isComboHeader / item.isComboDish
     from the backend's enrichItems + enrichCombos.
  ══════════════════════════════════════════ */
  y = 184;

  fill(doc, C.red);
  rrect(doc, PAD, y, 4, 16, 2);
  color(doc, C.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(11);
  text(doc, 'Order Items', PAD + 12, y + 11.5);
  y += 22;

  const tableW = W - PAD * 2;
  const cols = [
    { label: '#',          width: 24,            align: 'center' },
    { label: 'Item',       width: tableW - 286,  align: 'left'   },
    { label: 'Size',       width: 44,            align: 'center' },
    { label: 'Taxable',    width: 34,            align: 'center', isGstCol: true },
    { label: 'Qty',        width: 28,            align: 'center' },
    { label: 'Unit Price', width: 62,            align: 'right'  },
    { label: 'Disc.',      width: 38,            align: 'center' },
    { label: 'Amount',     width: 56,            align: 'right'  },
  ];

  const { rows: tableRows, rowMeta } = shapeItemRows(items || []);

  y = drawTable(doc, PAD, y, cols, tableRows, { rowH: 21, headH: 23 }, rowMeta);

  /* ══════════════════════════════════════════
     4 — TAX BOX (left) + TOTALS (right)
     Built from f.taxBreakdown — the tax lines as recorded in
     TaxCollection for THIS order, not from any live config.
  ══════════════════════════════════════════ */
  y += 18;

  const gstW     = 230;
  const totalsW  = W - PAD * 2 - gstW - 14;
  const totalsX  = PAD + gstW + 14;

  const taxTableHeadH = 19;
  const taxTableRowH  = 18;
  const taxRowCount   = Math.max(f.taxBreakdown.length, 1) + 1; // +1 for the "Total" row
  const taxBoxHeaderOffset = 24;
  const taxBoxBottomPad    = 10;
  const taxBoxH = taxBoxHeaderOffset
    + taxTableHeadH
    + taxTableRowH * taxRowCount
    + taxBoxBottomPad;

  fill(doc, C.light);
  stroke(doc, C.border);
  doc.setLineWidth(0.5);
  rrect(doc, PAD, y, gstW, taxBoxH, 5, 'FD');

  fill(doc, C.red);
  rrect(doc, PAD, y, 4, taxBoxH, 2);

  color(doc, C.dark);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(9);
  text(doc, 'Tax Summary', PAD + 14, y + 16);

  const gstCols = [
    { label: 'Component', width: 70,  align: 'left'  },
    { label: 'Rate',      width: 36,  align: 'center' },
    { label: 'Base Amt',  width: 64,  align: 'right'  },
    { label: 'Tax Amt',   width: 56,  align: 'right'  },
  ];
  const gstRows = f.taxBreakdown.length > 0
    ? [
        ...f.taxBreakdown.map((t) => [
          t.label,
          `${t.rate.toFixed(1)}%`,
          fmtI(f.taxableSub),
          fmtI(t.amount),
        ]),
        ['Total', `${f.combinedTaxRate.toFixed(1)}%`, '', fmtI(f.totalTax)],
      ]
    : [
        ['No tax record found', '—', '—', '—'],
        ['Total', '—', '', fmtI(f.totalTax)],
      ];

  drawTable(doc, PAD + 8, y + taxBoxHeaderOffset, gstCols, gstRows, {
    rowH: taxTableRowH, headH: taxTableHeadH,
    fontSize: 8, headFontSize: 7.5,
    headBgColor: C.headBg, headColor: C.dark,
    altRow: false,
  });

  /* ── Totals stack ── */
  const totRows = [
    { label: 'Non-Taxable Subtotal', value: fmt(f.nonTaxableSub) },
    { label: 'Taxable Subtotal',     value: fmt(f.taxableSub)    },
    ...f.taxBreakdown.map((t) => ({
      label: `${t.label} (${t.rate.toFixed(1)}%)`,
      value: fmt(t.amount),
    })),
  ];
  const totRowH = 22;
  let ty = y;

  totRows.forEach((row, i) => {
    const bg = i % 2 === 0 ? C.white : C.light;
    fill(doc, bg);
    rect(doc, totalsX, ty, totalsW, totRowH);

    color(doc, C.mid);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(8.5);
    text(doc, row.label, totalsX + 8, ty + totRowH / 2 + 3);
    text(doc, row.value, totalsX + totalsW - 8, ty + totRowH / 2 + 3, { align: 'right' });

    stroke(doc, C.border);
    doc.setLineWidth(0.3);
    doc.line(totalsX, ty + totRowH, totalsX + totalsW, ty + totRowH);
    ty += totRowH;
  });

  stroke(doc, C.border);
  doc.setLineWidth(0.5);
  doc.rect(totalsX, y, totalsW, totRows.length * totRowH, 'S');

  const gtY = ty + 6;
  fill(doc, C.dark);
  rrect(doc, totalsX, gtY, totalsW, 34, 5);
  color(doc, [160, 175, 190]);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(9);
  text(doc, 'Grand Total', totalsX + 12, gtY + 21);
  color(doc, C.white);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(13);
  text(doc, fmt(f.grandTotal), totalsX + totalsW - 12, gtY + 22, { align: 'right' });

  /* ══════════════════════════════════════════
     5 — ESTIMATE NOTICE + SPECIAL INSTRUCTIONS
  ══════════════════════════════════════════ */
  y = Math.max(y + taxBoxH, gtY + 46) + 22;

  if (f.isEstimate) {
    fill(doc, C.amberLight);
    stroke(doc, [253, 230, 138]);
    doc.setLineWidth(0.5);
    rrect(doc, PAD, y, W - PAD * 2, 30, 5, 'FD');

    color(doc, [146, 64, 14]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    text(doc, 'No tax record was found for this order — totals shown are estimated, not the recorded tax breakdown.', PAD + 10, y + 18, { maxWidth: W - PAD * 2 - 20 });
    y += 42;
  }

  if (order.specialInstructions) {
    fill(doc, C.amberLight);
    stroke(doc, [253, 230, 138]);
    doc.setLineWidth(0.5);
    rrect(doc, PAD, y, W - PAD * 2, 44, 5, 'FD');

    color(doc, [146, 64, 14]);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(7.5);
    text(doc, 'SPECIAL INSTRUCTIONS', PAD + 10, y + 13);

    doc.setFont('helvetica', 'normal');
    doc.setFontSize(9);
    color(doc, [120, 53, 15]);
    text(doc, order.specialInstructions, PAD + 10, y + 28, { maxWidth: W - PAD * 2 - 20 });
    y += 56;
  }

  /* ══════════════════════════════════════════
     6 — TIMESTAMPS ROW
  ══════════════════════════════════════════ */
  y += 8;
  const tsItems = [
    { label: 'Placed At',      value: formatDate(invoice.placedAt)    },
    { label: 'Completed At',   value: formatDate(invoice.completedAt) },
    { label: 'Payment Method', value: order.paymentMethod || '—'      },
    { label: 'Order ID',       value: String(invoice.orderId || '—')  },
  ];

  const tsW = (W - PAD * 2) / tsItems.length;
  tsItems.forEach((ts, i) => {
    const tx = PAD + i * tsW;
    fill(doc, i % 2 === 0 ? C.light : C.white);
    rect(doc, tx, y, tsW, 34);

    color(doc, [150, 160, 175]);
    doc.setFont('helvetica', 'normal');
    doc.setFontSize(7);
    text(doc, ts.label.toUpperCase(), tx + tsW / 2, y + 12, { align: 'center' });

    color(doc, C.dark);
    doc.setFont('helvetica', 'bold');
    doc.setFontSize(ts.label === 'Order ID' ? 6.5 : 8.5);
    text(doc, ts.value, tx + tsW / 2, y + 26, { align: 'center' });
  });

  stroke(doc, C.border);
  doc.setLineWidth(0.5);
  doc.rect(PAD, y, W - PAD * 2, 34, 'S');
  y += 50;

  /* ══════════════════════════════════════════
     7 — QUOTE FOOTER
  ══════════════════════════════════════════ */
  stroke(doc, C.border);
  doc.setLineWidth(0.6);
  doc.setLineDash([3, 3], 0);
  doc.line(PAD, y, W - PAD, y);
  doc.setLineDash([], 0);
  y += 16;

  fill(doc, C.light);
  rrect(doc, PAD, y, W - PAD * 2, 52, 6);

  color(doc, C.red);
  doc.setFont('helvetica', 'bold');
  doc.setFontSize(30);
  text(doc, '\u201C', PAD + 14, y + 36);

  color(doc, C.mid);
  doc.setFont('helvetica', 'italic');
  doc.setFontSize(10);
  const quote = randomQuote();
  text(doc, quote, PAD + 46, y + 20, { maxWidth: W - PAD * 2 - 60 });

  y += 64;

  color(doc, [170, 180, 195]);
  doc.setFont('helvetica', 'normal');
  doc.setFontSize(7.5);
  text(
    doc,
    `Invoice generated on ${new Date().toLocaleString('en-IN')}  ·  This is a computer-generated invoice and does not require a signature.`,
    W / 2, y, { align: 'center' }
  );

  /* ── Save ── */
  const safeName = (restaurant.name || 'Order').replace(/[^a-zA-Z0-9_\- ]/g, '').replace(/\s+/g, '_');
  doc.save(`Invoice_${safeName}_${invNum}.pdf`);
};

/* ═══════════════════════════════════════════════════════════════
   MAIN EXPORT
   Accepts either:
     - an order object (uses order.dailyOrderNumber, falling back to order._id)
     - a raw order number / order id (string or number) directly
   Fetches the invoice payload from the backend — which sources all
   tax figures from the order's TaxCollection snapshot — then builds
   the PDF from that response only. No live tax config is touched.
═══════════════════════════════════════════════════════════════ */
export const generateInvoice = async (order, { onStart, onSuccess, onError } = {}) => {
  
  if (!order._id) {
    const err = new Error('generateInvoice: no order number or order id provided');
    onError?.(err);
    throw err;
  }

  try {
    onStart?.();

    const res = await fetch(`${host}/invoice/generate/${order._id}`);
    if (!res.ok) {
      const body = await res.json().catch(() => ({}));
      throw new Error(body.message || `Failed to fetch invoice (${res.status})`);
    }

    const data = await res.json();
    buildPDF(data);
    onSuccess?.(data);
    return data;
  } catch (err) {
    onError?.(err);
    throw err;
  }
};